import {
  Drawer,
  Group,
  Stack,
  Text,
  TextInput,
  useMantineTheme,
  Button,
  Box,
  ActionIcon,
  ScrollArea,
  Title,
  CloseButton,
} from "@mantine/core";

import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { AlertCircle, Plus, Trash } from "tabler-icons-react";
import driverService from "../api/driverService";
import logService from "../api/logService";
import { i18n } from "../config/translation";
import { Driver } from "../model/Driver";
import { getCompany } from "../store/company";
import ImageInput from "./ImageInput";

interface NewDriverFormProps {
  onClose: () => void;
  onUpdate: (driver: Driver) => void;
}

const NewDriverForm: React.FC<NewDriverFormProps> = ({ onClose, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const theme = useMantineTheme();
  const company = useSelector(getCompany);
  const form = useForm({
    initialValues: {
      name: "",
      phone: [""],
      avatar: undefined,
    },

    validate: {
      name: (value) =>
        value.length < 3
          ? i18n.t("formMin", { field: i18n.t("name"), num: 3 })
          : null,
    },
  });

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const result = await driverService.createDriver({
        ...values,
        company: company._id,
      });
      onUpdate(result.data);
      showNotification({
        title: i18n.t("createdSuccessfully"),
        message: i18n.t("isCreated", { name: values.name }),
        color: "indigo",
      });
      onClose();
    } catch (ex) {
      if (ex.response.status === 400 || ex.response.status === 404) {
        showNotification({
          title: i18n.t("somethingWrong"),
          message: i18n.t("tryAgain"),
          color: "red",
        });
      }
      logService.log(ex);
    }
    setLoading(false);
  };

  const textInputStyle = {
    filledVariant: {
      backgroundColor: theme.colors.background[0],
      border: `1px solid ${theme.colors.border[0]}`,
    },
    input: {
      "&::placeholder": { color: theme.colors.dark[1] },
    },
  };

  const fields = form.values.phone.map((item, index) => (
    <Group key={index} mt="xs">
      <TextInput
        styles={textInputStyle}
        placeholder={i18n.t("phone")}
        radius={"xl"}
        required
        sx={{ flex: 1 }}
        value={item}
        onChange={(event) => {
          const newData = [...form.values.phone];
          newData[index] = event.currentTarget.value;
          form.setFieldValue("phone", newData);
        }}
      />
      <ActionIcon
        color="red"
        variant="hover"
        onClick={() => {
          if (form.values.phone.length > 1) {
            const newData = [...form.values.phone];
            newData.splice(index, 1);
            form.setFieldValue("phone", newData);
          } else {
            form.setFieldValue("phone", [""]);
          }
        }}
      >
        <Trash size={16} />
      </ActionIcon>
    </Group>
  ));

  return (
    <>
      <Group mb="lg" position="apart">
        <Title order={3}>{i18n.t("addNewDriver")}</Title>
        <CloseButton onClick={onClose} />
      </Group>
      <ScrollArea
        style={{ height: "calc(100vh - 100px)" }}
        scrollbarSize={4}
        type="scroll"
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack spacing={"xl"}>
            <TextInput
              radius={"xl"}
              label={i18n.t("name")}
              value={form.values.name}
              placeholder={i18n.t("name")}
              onChange={(event) =>
                form.setFieldValue("name", event.currentTarget.value)
              }
              required
              styles={textInputStyle}
              {...form.getInputProps("name")}
            />
            <Box>
              <Text size="sm">{i18n.t("contacts")}</Text>
              {fields}
              <Button
                mt={"xs"}
                size="xs"
                radius={"lg"}
                color="indigo"
                variant="filled"
                leftIcon={<Plus size={14} />}
                onClick={() => {
                  const newData = [...form.values.phone];
                  newData.push("");
                  form.setFieldValue("phone", newData);
                }}
                style={{ zIndex: 10 }}
              >
                {i18n.t("addContact")}
              </Button>
            </Box>

            <Box>
              <Group pb="xs" position="apart">
                <Text size="sm">{i18n.t("image")}</Text>
              </Group>
              <ImageInput
                onDrop={(file) => form.setFieldValue("avatar", file)}
                value={form.values.avatar}
              />
            </Box>
          </Stack>
          <Group position="right" mt="lg">
            <Button
              type="submit"
              color="indigo"
              radius={"lg"}
              fullWidth
              mt="lg"
              loading={loading}
            >
              {i18n.t("save")}
            </Button>
          </Group>
        </form>
      </ScrollArea>
    </>
  );
};

export default NewDriverForm;
